/**
 * Typed API client — the ONLY way web code talks to the server.
 * Every endpoint here exists in src/server/routes/* (see the shared contract
 * in @shared/types). Do NOT invent endpoints; extend both sides if needed.
 */
import type {
  AppSettings,
  AuthStatus,
  EventItem,
  Heartbeat,
  Incident,
  IncidentInput,
  MaintenanceInput,
  MaintenanceWindow,
  MonitorDetail,
  MonitorGroup,
  MonitorInput,
  MonitorListItem,
  MonitorStats,
  NotificationChannel,
  NotificationInput,
  NotificationType,
  PublicStatusPage,
  StatsPeriod,
  StatusPage,
  StatusPageDetail,
  StatusPageInput,
} from "@shared/types";

export class ApiError extends Error {
  status: number;
  needsPassword: boolean;

  constructor(status: number, message: string, needsPassword = false) {
    super(message);
    this.status = status;
    this.needsPassword = needsPassword;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (!res.ok) {
    let message = res.statusText || `Request failed (${res.status})`;
    let needsPassword = false;
    try {
      const body: unknown = await res.json();
      if (body && typeof body === "object") {
        if ("error" in body && typeof body.error === "string") message = body.error;
        if ("needsPassword" in body && body.needsPassword === true) needsPassword = true;
      }
    } catch {
      // non-JSON error body — keep statusText
    }
    throw new ApiError(res.status, message, needsPassword);
  }
  if (res.status === 204) {
    // no content — caller's T must be void
    return undefined as T;
  }
  // Trusted boundary: server responses follow the shared contract types.
  const data = (await res.json()) as T;
  return data;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: "PUT", body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: "DELETE" });

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const getAuthStatus = () => get<AuthStatus>("/api/auth/status");
export const setup = (username: string, password: string) =>
  post<{ ok: true; username: string }>("/api/auth/setup", { username, password });
export const login = (username: string, password: string) =>
  post<{ ok: true; username: string }>("/api/auth/login", { username, password });
export const logout = () => post<{ ok: true }>("/api/auth/logout");
export const changePassword = (currentPassword: string, newPassword: string) =>
  post<{ ok: true }>("/api/auth/change-password", { currentPassword, newPassword });

// ---------------------------------------------------------------------------
// Monitors
// ---------------------------------------------------------------------------

export const listMonitors = () => get<MonitorListItem[]>("/api/monitors");
export const getMonitor = (id: number) => get<MonitorDetail>(`/api/monitors/${id}`);
export const createMonitor = (input: MonitorInput) => post<MonitorDetail>("/api/monitors", input);
export const updateMonitor = (id: number, input: MonitorInput) =>
  put<MonitorDetail>(`/api/monitors/${id}`, input);
export const deleteMonitor = (id: number) => del<{ ok: true }>(`/api/monitors/${id}`);
export const pauseMonitor = (id: number) => post<{ ok: true }>(`/api/monitors/${id}/pause`);
export const resumeMonitor = (id: number) => post<{ ok: true }>(`/api/monitors/${id}/resume`);
export const resetPushToken = (id: number) => post<MonitorDetail>(`/api/monitors/${id}/reset-token`);
export const getHeartbeats = (id: number, limit = 50) =>
  get<Heartbeat[]>(`/api/monitors/${id}/heartbeats?limit=${limit}`);
export const getMonitorStats = (id: number, period: StatsPeriod) =>
  get<MonitorStats>(`/api/monitors/${id}/stats?period=${period}`);
export const getMonitorEvents = (id: number, limit = 50) =>
  get<EventItem[]>(`/api/monitors/${id}/events?limit=${limit}`);
export const listEvents = (limit = 25) => get<EventItem[]>(`/api/events?limit=${limit}`);

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export const listGroups = () => get<MonitorGroup[]>("/api/groups");
export const createGroup = (name: string) => post<MonitorGroup>("/api/groups", { name });
export const updateGroup = (id: number, name: string) => put<MonitorGroup>(`/api/groups/${id}`, { name });
export const deleteGroup = (id: number) => del<{ ok: true }>(`/api/groups/${id}`);

// ---------------------------------------------------------------------------
// Notification channels
// ---------------------------------------------------------------------------

export const listNotifications = () => get<NotificationChannel[]>("/api/notifications");
export const createNotification = (input: NotificationInput) =>
  post<NotificationChannel>("/api/notifications", input);
export const updateNotification = (id: number, input: NotificationInput) =>
  put<NotificationChannel>(`/api/notifications/${id}`, input);
export const deleteNotification = (id: number) => del<{ ok: true }>(`/api/notifications/${id}`);
export const testNotification = (type: NotificationType, config: Record<string, unknown>) =>
  post<{ ok: true }>("/api/notifications/test", { type, config });

// ---------------------------------------------------------------------------
// Status pages (admin)
// ---------------------------------------------------------------------------

export const listStatusPages = () => get<StatusPage[]>("/api/status-pages");
export const getStatusPage = (id: number) => get<StatusPageDetail>(`/api/status-pages/${id}`);
export const createStatusPage = (input: StatusPageInput) =>
  post<StatusPageDetail>("/api/status-pages", input);
export const updateStatusPage = (id: number, input: StatusPageInput) =>
  put<StatusPageDetail>(`/api/status-pages/${id}`, input);
export const deleteStatusPage = (id: number) => del<{ ok: true }>(`/api/status-pages/${id}`);
export const createIncident = (pageId: number, input: IncidentInput) =>
  post<Incident>(`/api/status-pages/${pageId}/incidents`, input);
export const updateIncident = (
  pageId: number,
  incidentId: number,
  input: Partial<IncidentInput> & { pinned?: boolean; resolved?: boolean },
) => put<Incident>(`/api/status-pages/${pageId}/incidents/${incidentId}`, input);
export const deleteIncident = (pageId: number, incidentId: number) =>
  del<{ ok: true }>(`/api/status-pages/${pageId}/incidents/${incidentId}`);

// ---------------------------------------------------------------------------
// Public status pages
// ---------------------------------------------------------------------------

export const getPublicStatus = (slug: string) =>
  get<PublicStatusPage>(`/api/status/${encodeURIComponent(slug)}`);
export const authStatusPage = (slug: string, password: string) =>
  post<{ ok: true }>(`/api/status/${encodeURIComponent(slug)}/auth`, { password });
export const resolveStatusHost = (host: string) =>
  get<{ slug: string | null }>(`/api/status/resolve?host=${encodeURIComponent(host)}`);

// ---------------------------------------------------------------------------
// Maintenance windows
// ---------------------------------------------------------------------------

export const listMaintenance = () => get<MaintenanceWindow[]>("/api/maintenance");
export const createMaintenance = (input: MaintenanceInput) =>
  post<MaintenanceWindow>("/api/maintenance", input);
export const updateMaintenance = (id: number, input: MaintenanceInput) =>
  put<MaintenanceWindow>(`/api/maintenance/${id}`, input);
export const deleteMaintenance = (id: number) => del<{ ok: true }>(`/api/maintenance/${id}`);

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const getSettings = () => get<AppSettings>("/api/settings");
export const updateSettings = (settings: Pick<AppSettings, "retentionDays">) =>
  put<AppSettings>("/api/settings", settings);
export const rotateMcpKey = () => post<{ mcpApiKey: string }>("/api/settings/mcp-key");
export const disableMcp = () => del<{ ok: boolean }>("/api/settings/mcp-key");
